import QNShare from "@assets/actions/share.svg";
import QNShareLight from "@assets/actions/share-light.svg";
import shareMobileLogo from "@assets/social_media/share-mb.svg";
import ShareSocial, {
  type SocialVariant,
} from "@blocks/ShareSocial/ShareSocial";
import { isMobileOrTablet } from "@helper/utils";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";

export type ShareSocialProps = {
  buttonTitle?: string;
  socialIconList: string[];
  pagePath: string;
  deskClassName?: string;
  mbPopupClassName?: string;
  mbClassName?: string;
  quickNews?: boolean;
  quickNewsLight?: boolean;
  onClickGaEvent?: () => void;
};

export default function ShareSocialButton({
  buttonTitle = "SHARE",
  socialIconList,
  pagePath,
  deskClassName,
  mbPopupClassName,
  mbClassName,
  quickNews = false,
  quickNewsLight = false,
  onClickGaEvent,
}: ShareSocialProps) {
  const { isScreenLG } = useWindowSize();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const shareData = {
    url: pagePath,
  };

  // Toggle the dropdown menu
  const toggleDropdown = () => {
    if (isMobileOrTablet()) {
      try {
        void navigator.share(shareData);
      } catch (err) {
        setIsOpen((prev) => !prev);
      }
    } else {
      setIsOpen((prev) => !prev);
    }

    if (onClickGaEvent) {
      onClickGaEvent();
    }
  };

  // Close the dropdown if clicked outside
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    // Add event listener to the document
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Clean up the event listener on unmount
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderSocialIcons = () =>
    socialIconList.map((variant, index) => (
      <ShareSocial
        variant={variant as SocialVariant}
        pagePath={pagePath}
        key={index}
      />
    ));

  const shareIcon = quickNews
    ? quickNewsLight
      ? QNShareLight
      : QNShare
    : shareMobileLogo;

  const popupClasses = cx(
    "flex gap-5 bg-white-100 z-10 rounded-3xl mr-sm",
    quickNews && "border border-red-100 px-1 py-1 -top-16 -right-5",
    mbPopupClassName,
  );

  return (
    <>
      {!quickNews && isScreenLG ? (
        <div
          className={cx("flex gap-5", deskClassName)}
          data-testid="share-wrapper"
        >
          {renderSocialIcons()}
        </div>
      ) : (
        <div
          ref={dropdownRef}
          className={mbClassName}
          data-testid="share-wrapper"
        >
          <div className={quickNews ? "relative" : undefined}>
            <button
              onClick={toggleDropdown}
              className={cx(
                "dropdown-button flex rounded-3xl",
                !quickNews && "py-2 px-3 bg-pink-500",
              )}
            >
              <img
                src={shareIcon}
                alt="share-mb"
                width={24}
                height={24}
                className="mr-1.5"
              />
              {!quickNews && (
                <span className="font-primary font-semibold text-xs text-white-100">
                  {buttonTitle}
                </span>
              )}
            </button>

            {isOpen && (
              <div className={popupClasses}>{renderSocialIcons()}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
